'use client';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Modal from '@/components/ui/modal';
import type { Buyer, LanguageIsoCode, Program } from '@/graphql/graphql';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import AppButton from '@/components/ui/app-button';
import { useCart } from '@/context/auth/cart-context';
import { useUserData } from '@/context/auth/user-data-context';
import { useTranslations } from 'next-intl';

interface ProgramProps {
    program: Program;
    excludeEpisodeIds?: string[];
    onClose: () => void;
    onError: (message: string) => void;
}

const getProgramAudioLanguages = (program: Program, user: Buyer): string[] => {
    // Collect Program audio languages
    const allLanguages = program.audioLanguages.languages.map((lang: LanguageIsoCode) => lang.toLowerCase());
    let languages = allLanguages;

    const buyerBroadcastLanguage = (user as Buyer).broadcastLanguage;

    // Filter to keep only broadcast language and main audio language
    languages = languages.filter(
        (lang: string) =>
            lang === program.audioLanguages.main.toLowerCase() || lang === buyerBroadcastLanguage?.toLowerCase(),
    );

    // If none of broadcast language or main audio language are available, return all languages
    if (languages.length === 0) {
        languages = allLanguages;
    }

    return languages;
};

export default function ModalCartPage({ program, excludeEpisodeIds, onClose, onError }: ProgramProps) {
    const [currentStep, setCurrentStep] = useState<'audioSelection' | 'confirmation'>('audioSelection');
    const [selectedModalCartAudio, setSelectedModalCartAudio] = useState('');
    const [modalCartOpen, setModalCartOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tl = useTranslations('IsoLanguage');
    const t = useTranslations('AddCartModal');
    const { user } = useUserData();
    const { addToCart } = useCart();
    const userBroadcastLang = (user as Buyer).broadcastLanguage as LanguageIsoCode;

    const audioLangs = useMemo(() => getProgramAudioLanguages(program, user as Buyer), [program, userBroadcastLang]);
    const mainLang = program.audioLanguages?.main.toLowerCase() as LanguageIsoCode;

    const dropdownOptions = useMemo(() => {
        if (audioLangs.length === 1) return [];
        return audioLangs;
    }, [audioLangs]);

    useEffect(() => {
        if (audioLangs.length === 0) return;

        setSelectedModalCartAudio((current) => {
            if (current && audioLangs.includes(current)) {
                return current;
            }

            return audioLangs[0];
        });
    }, [audioLangs]);

    const handleAddToCart = async () => {
        try {
            const language = selectedModalCartAudio.toUpperCase() as LanguageIsoCode;
            await addToCart(program.identifier.id, language, excludeEpisodeIds);
            setCurrentStep('confirmation');
            return;
        } catch (error) {
            const message = error instanceof Error ? error.message : t('errors.genericError');
            onError(message);
        }
    };

    const handleCloseModal = () => {
        setCurrentStep('audioSelection');
        setSelectedModalCartAudio('');
        onClose();
    };

    const tiltleH3ClassName = 'text-center font-bold text-[24px] pb-[10px]';
    const pClassName = 'text-center font-normal text-[18px] text-shadow-[0_4px_4px_rgb(0_0_0_/_0.25)]';
    const secondaryInfoClassName = 'mt-10 text-center font-normal text-sm text-shadow-[0_4px_4px_rgb(0_0_0_/_0.25)]';

    return (
        <>
            {currentStep === 'audioSelection' && (
                <Modal
                    rounded="rounded-[8px]"
                    modalPadding="pt-[38px] px-[41px] pb-[54px]"
                    closePosition="top-[20px] right-[41px]"
                    isOpen={true}
                    onClose={handleCloseModal}
                    ModalSize="max-w-[580px]"
                >
                    <div className="aspect-video w-full text-center flex flex-col">
                        <h3 className={tiltleH3ClassName}>{t('title')}</h3>

                        {audioLangs.length === 1 ? (
                            <>
                                <p className={pClassName}>{t('onlyOneAudio')}</p>
                                <p className={`${pClassName} mt-[19px] mb-[40px]`}>
                                    <span className="text-[#DD06AE] font-bold uppercase">
                                        {tl(audioLangs[0].toUpperCase() as LanguageIsoCode)} ({t('original')})
                                    </span>
                                </p>
                            </>
                        ) : (
                            <>
                                <p className={pClassName}>{t('selectLanguageTitle')}</p>
                                <div className="w-[238px] h-[46px] mx-auto mt-[28px] mb-[40px]">
                                    <DropdownMenu open={modalCartOpen} onOpenChange={setModalCartOpen}>
                                        <DropdownMenuTrigger asChild>
                                            <div
                                                ref={triggerRef}
                                                className={`
                                                    w-full flex items-center justify-between
                                                    text-[14px] font-normal
                                                    px-[13px] py-[11px] pl-[20px]
                                                    rounded-[8px]
                                                    cursor-pointer
                                                    border-1 bg-[rgba(255,255,255,0.1)]`}
                                            >
                                                {selectedModalCartAudio ? (
                                                    <>
                                                        {tl(selectedModalCartAudio.toUpperCase() as LanguageIsoCode)}
                                                        {selectedModalCartAudio === mainLang && ` (${t('original')})`}
                                                    </>
                                                ) : null}

                                                <ChevronDown className="ml-2 w-6 text-white" />
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            className="px-[10px] py-5"
                                            style={{
                                                minWidth: triggerRef.current?.offsetWidth || undefined,
                                            }}
                                        >
                                            {dropdownOptions.map((lang) => {
                                                const isMain = lang === mainLang;

                                                return (
                                                    <div
                                                        key={lang}
                                                        className={`last:mb-0 w-full px-[10px] py-[5px] cursor-pointer rounded-[8px] hover:bg-white/30 mb-1 transition-all duration-300 ${
                                                            selectedModalCartAudio === lang ? 'bg-white/30' : ''
                                                        }`}
                                                        onClick={() => {
                                                            setSelectedModalCartAudio(lang);
                                                            setModalCartOpen(false);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                setSelectedModalCartAudio(lang);
                                                                setModalCartOpen(false);
                                                            }
                                                        }}
                                                    >
                                                        {tl(lang.toUpperCase() as LanguageIsoCode)}
                                                        {isMain && ` (${t('original')})`}
                                                    </div>
                                                );
                                            })}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </>
                        )}

                        <h3 className={tiltleH3ClassName}>{t('subtitle')}</h3>
                        <p className={pClassName}>
                            {t.rich('subtitleText', {
                                currentLanguage: tl(userBroadcastLang.toUpperCase() as LanguageIsoCode),
                                highlight: (chunks: React.ReactNode) => (
                                    <b className="uppercase text-[#DD06AE]">{chunks}</b>
                                ),
                            })}
                        </p>
                        <AppButton
                            innerText={t('buttonLabel')}
                            btnBgColor="#DD06AE"
                            customClassName="mt-[28px] mx-auto font-medium text-[14px] py-[15px] px-[30px]"
                            onClick={handleAddToCart}
                        />
                        <p className={secondaryInfoClassName}>{t('asteriskText')}</p>
                    </div>
                </Modal>
            )}

            {currentStep === 'confirmation' && (
                <Modal
                    rounded="rounded-[8px]"
                    modalPadding="pt-[64px] px-[37px] pb-[44px]"
                    closePosition="top-[15px] right-[15px]"
                    isOpen={true}
                    onClose={handleCloseModal}
                    ModalSize="max-w-[560px]"
                >
                    <p className={pClassName}>{t('confirmation.title')}</p>
                    <div className="flex items-center justify-between mt-[47px]">
                        <AppButton
                            innerText={t('confirmation.buttonContinue')}
                            customClassName="border-1 text-[14px] font-medium"
                            onClick={handleCloseModal}
                        />
                        <AppButton
                            href="/cart"
                            innerText={t('confirmation.buttonCheck')}
                            btnBgColor="#DD06AE"
                            borderColor="#DD06AE"
                            customClassName="text-[14px] border-1 font-medium flex-1 ml-[16px]"
                        />
                    </div>
                </Modal>
            )}
        </>
    );
}
